import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { Cabinet } from '@/store/cabinet/cabinet.model';
import { CabinetSchema } from '@/store/cabinet/cabinet.validator';
import { CabinetAction, CabinetGetter } from '@/store/cabinet/cabinet.module';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'CabinetFormComponent',
  components: {
    Datepicker,
    BaseModalComponent,
  },
  props: {
    cabinetId: {
      type: Number,
    }
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => props.cabinetId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขข้อมูลหนังสือคำสั่ง' : 'เพิ่มข้อมูลหนังสือคำสั่ง');
    const cabinet = computed<Cabinet | undefined>(() => store.getters[CabinetGetter.BY_ID](props.cabinetId));
    const cabinetForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      name?: string | null;
      startDate?: Date | null;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      startDate: null,
    });
    const validator = computed(() => CabinetSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      startDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'startDate')),
    });
    const isValid = computed(() => !errors.name && !errors.startDate);
    const isLoading = computed<boolean>(() => store.getters[CabinetGetter.STATUS] === 'init' || store.getters[CabinetGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.cabinetId;
          formData.name = cabinet.value?.name;
          formData.startDate = cabinet.value?.startDate;
        }
        resolve();
      })
      .then(() => cabinetForm.value?.show());
    };

    // hide modal
    const hide = () => {
      cabinetForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.startDate = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(CabinetAction.UPDATE, formData)
            .then(() => store.dispatch(CabinetAction.FETCH)
              .then(() => hide()));
        } else {
          store.dispatch(CabinetAction.CREATE, formData)
            .then(() => store.dispatch(CabinetAction.FETCH)
              .then(() => hide()));
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(CabinetAction.DELETE, formData)
              .then(() => store.dispatch(CabinetAction.FETCH)
                .then(() => hide()));
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      cabinetForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  }
});
